import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router";
import { getQuestionnaire } from "api-client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PrimaryNavigationTabs } from "src/components/NavigationTabs";
import { useChildRouteSegment } from "src/hooks/useChildRouteSegment";
import { TabLink } from "src/layout/TabLink";
import { loaderHandler } from "src/lib/loaderHandler";
import {
  QuestionnaireBuilderContextProvider,
  QuestionnaireHeader,
} from "src/questionnaire";
import { AssignQuestionnaireModal } from "src/questionnaire/AssignQuestionnaireModal";

export const Route = createFileRoute(
  "/_auth/_coach/questionnaires/$questionnaireId",
)({
  component: RouteComponent,
  ssr: true,
  shouldReload: false,
  async loader({ params }) {
    return await loaderHandler(async () => {
      const questionnaire = await getQuestionnaire({
        path: {
          id: params.questionnaireId,
        },
      }).then((response) => {
        return response.data.data;
      });

      return {
        data: {
          questionnaire: questionnaire,
        },
        breadcrumb: {
          id: questionnaire.questionnaire.id,
          label: questionnaire.questionnaire.name,
          href: `/questionnaires/${params.questionnaireId}`,
        },
      };
    });
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { data } = Route.useLoaderData();
  const params = Route.useParams();
  const router = useRouter();
  const tabId = useChildRouteSegment() ?? "form";

  const questionnaireUrl = router.buildLocation({
    to: "/questionnaires/$questionnaireId",
    params: {
      questionnaireId: params.questionnaireId,
    },
  });

  const submissionsUrl = router.buildLocation({
    to: "/questionnaires/$questionnaireId/submissions",
    params: {
      questionnaireId: params.questionnaireId,
    },
  });

  const navigationContextValue = useMemo(() => {
    return {
      tabs: [
        {
          tabId: "form",
          label: t("form"),
          href: questionnaireUrl.pathname,
        },
        {
          tabId: "submissions",
          label: t("responses"),
          href: submissionsUrl.pathname,
        },
      ],
      components: {
        Link: TabLink,
      },
      getCurrentTabId: () => {
        return tabId;
      },
    };
  }, [tabId, t, questionnaireUrl, submissionsUrl]);

  return (
    <QuestionnaireBuilderContextProvider
      key={data.questionnaire.questionnaire.id}
      questionnaire={data.questionnaire}
    >
      <PrimaryNavigationTabs.Context.Provider value={navigationContextValue}>
        <PrimaryNavigationTabs.TabsRoot className="w-full h-full flex flex-col">
          <div className="px-2 items-center justify-between top-0 flex border-b sticky bg-white h-sztama-nav-tabs-height z-10">
            <QuestionnaireHeader
              questionnaireId={params.questionnaireId}
              backHref="/questionnaires"
            />
            <PrimaryNavigationTabs.TabsList className="capitalize absolute left-1/2 -translate-x-1/2" />
            <AssignQuestionnaireModal />
          </div>
          <PrimaryNavigationTabs.TabsContent className="w-full m-0 h-full overflow-auto overscroll-none">
            <Outlet />
          </PrimaryNavigationTabs.TabsContent>
        </PrimaryNavigationTabs.TabsRoot>
      </PrimaryNavigationTabs.Context.Provider>
    </QuestionnaireBuilderContextProvider>
  );
}
